/* eslint-disable curly */
/* eslint-disable no-empty */
/* eslint-disable prettier/prettier */
import { useGtm } from "@gtm-support/vue-gtm";
import { createSharedComposable } from "@vueuse/core";
import { Logger } from "@/core/utilities";
import { useFullCart } from "../cart";
import type {CartExtension, CustomerOrderTypeExtension, GetFullCartQuery, MoneyType} from "@/core/api/graphql/types";
import type { ApolloQueryResult } from "@apollo/client/core/types";
import type { ComputedRef } from "vue";

export const useThorlabsGtm = createSharedComposable(_useThorlabsGtm);

function _useThorlabsGtm(cartParam? : ComputedRef<CartExtension | undefined>,
                        forceFetchParam? : () => Promise<GetFullCartQuery | ApolloQueryResult<GetFullCartQuery> | undefined>)
{
    const gtm = useGtm();

    if (!gtm)
    {
        Logger.warn("GTM is not initailized (gtm object in gtm.ts is undefined). Aborting initializing thorlabsGtmComposable...")
        return {
            gtmSendTabViewEvent: () => {},
            gtmSendSearchEvent: () => {},
            gtmSendViewCartEventAsync: () => Promise.resolve(),
            gtmSendBeginCheckoutEventAsync: () => Promise.resolve(),
            gtmSendAddShippingInfoEventAsync: () => Promise.resolve(),
            gtmSendAddPaymentInfoEventAsync: () => Promise.resolve(),
            gtmSendPurchaseEvent: () => Promise.resolve(),
            gtmSendEmptyCartEventAsync: () => Promise.resolve(),
            gtmSendForwardCartEventAsync: () => Promise.resolve(),
            gtmSendSaveCartEventAsync: () => Promise.resolve(),
            gtmSendExportCartEventAsync: () => Promise.resolve(),
            gtmSendImportCartEventAsync: () => Promise.resolve(),
            gtmSendQuoteEvent: () => Promise.resolve(),
            gtmSendGenerateLeadEvent: () => Promise.resolve(),
            gtmSendAutocompleteSearchEvent: () => {},
            gtmSendFeedbackFormClosed: () => {},
            gtmSendFormStartEvent: () => {}
        }
    }

    let cart, forceFetch;
    if (cartParam && forceFetchParam)
    {
        cart = cartParam;
        forceFetch = forceFetchParam;
    }
    else
    {
        ({cart, forceFetch} = useFullCart());
    }


    //Inner function that prepares items array for gtm cart events
    const _gtmPrepareCartItemsDataAsync = async () : Promise<IGTMCartItem[]> =>
    {
        const gtmItems : IGTMCartItem[] = [];

        //Fetch cart if needed
        if(!cart.value)
            await forceFetch();


        for (const lineItem of cart.value?.items || [])
        {
            const price : MoneyType = lineItem?.placedPrice;
            const usdPrice = price?.currency?.code === "USD" ? price : lineItem.product?.allCurrenciesPricesTL.find( p => p.list.currency.code === "USD" )?.list;

            const categoryBreadcrumbsDescriptionArray = JSON.parse(lineItem.product?.descriptionsWithFallbacksTL?.find( d => d.reviewType === "Breadcrumb" )?.content || '[]') as string[][];
            let categoryBreadcrumbs = categoryBreadcrumbsDescriptionArray.map( (c) => c[0] );

            //Cut out 'Main Visual Navigation' from breadcrumbs and reverse array
            categoryBreadcrumbs = categoryBreadcrumbs.length > 1 ? categoryBreadcrumbs.slice(0, -1).reverse() : [];

            const titleFromDescriptions = lineItem.product?.descriptionsWithFallbacksTL?.find( d => d.reviewType === "Title" )?.content;

            const gtmItem: IGTMCartItem =
            {
                item_id: lineItem.product?.code,
                item_name: titleFromDescriptions,
                item_category:  categoryBreadcrumbs.at(0), //Only some of these will have values, others will be undefined and there won't be IndexOutOfRange exception
                item_category2: categoryBreadcrumbs.at(1),
                item_category3: categoryBreadcrumbs.at(2),
                item_category4: categoryBreadcrumbs.at(3),
                item_category5: categoryBreadcrumbs.at(4),
                item_category6: categoryBreadcrumbs.at(5),
                item_category7: categoryBreadcrumbs.at(6),
                price: usdPrice?.amount ?? NaN, //Always use USD price for GTM events and optionally add real currency in custom fields
                quantity: lineItem.quantity,
            };


            //Add real currency and price if it is not USD
            if (price?.currency?.code !== "USD")
            {
                gtmItem.foreignCurrencyCode = price?.currency?.code;
                gtmItem.foreignCurrencyAmount = price?.amount;
            }

            gtmItems.push(gtmItem);
        }

        return gtmItems;
    };//End of _gtmPrepareCartItemsData()

    //Inner function which sends various cart GTM events
    const _gtmSendCartEventAsync = async (eventName : string) =>
    {
        gtm.push( await _gtmPrepareCartEventAsync(eventName) );
    };//End of _gtmSendCartEventAsync()

    //Inner function which prepares cart GTM data
    const _gtmPrepareCartEventAsync = async (eventName : string) =>
    {
        if (!cart.value)
            await forceFetch();

        const gtmCartItems = await _gtmPrepareCartItemsDataAsync();

        const cartEventData : IGTMCartEvent = _gtmPrepareCartEventWithoutItems(eventName);
        cartEventData.ecommerce
          ? cartEventData.ecommerce.items = gtmCartItems
          : cartEventData.ecommerce = {
              currency: "USD",
              items: gtmCartItems,
              value: gtmCartItems.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 0), 0),};
        return cartEventData;
    };//End of _gtmPrepareCartEventAsync()

    //Inner function which prepares cart GTM data (but without listing cart items)
    const _gtmPrepareCartEventWithoutItems = (eventName : string) =>
    {
        const usdSubTotalAmount = getAnalyticsCartSubtotalForCurrency(cart);
        const realSubTotal = cart.value?.subTotal;

        const cartEventData : IGTMCartEvent = {
            ecommerce: {
                currency: "USD",
                value: usdSubTotalAmount || 0,
            },
            event: eventName,
        };

        if (realSubTotal?.currency.code !== "USD")
        {
            //Add foreign currency as custom fields to the GTM event
            cartEventData.foreignCurrencyCode = realSubTotal?.currency.code;
            cartEventData.foreignCurrencyAmount = realSubTotal?.amount;
        }

        return cartEventData;
    };//End of _gtmPrepareCartEventWithoutItems()

    //Inner function used to send either "purchase" or "quote" event to GTM (both are created from order instance)
    const _gtmSendCheckoutEvent = (order : CustomerOrderTypeExtension, eventName: string) =>
    {
        if (!order)
        {
            Logger.warn(`GTM - ${eventName} event was not sent because order is undefined`);
            return;
        }

        const gtmItems : IGTMCartItem[] = [];

        for (const lineItem of order.items || [])
        {
            //Make sure to always get USD currency price for line items
            const price = order.subTotal.currency.code === "USD" ? lineItem?.price : lineItem.product?.allCurrenciesPricesTL.find( p => p.list.currency.code === "USD" )?.list;
            //logger.debug(lineItem);

            const categoryBreadcrumbsDescriptionArray = JSON.parse(lineItem.product?.descriptionsWithFallbacksTL?.find( d => d.reviewType === "Breadcrumb" )?.content || '[]') as string[][];
            let categoryBreadcrumbs = categoryBreadcrumbsDescriptionArray.map( (c) => c[0] );

            //Cut out 'Main Visual Navigation' from breadcrumbs and reverse array
            categoryBreadcrumbs = categoryBreadcrumbs.length > 1 ? categoryBreadcrumbs.slice(0, -1).reverse() : [];

            const titleFromDescriptions = lineItem.product?.descriptionsWithFallbacksTL?.find( d => d.reviewType === "Title" )?.content;

            const gtmItem: IGTMCartItem =
            {
                item_id: lineItem.sku,
                item_name: titleFromDescriptions,
                item_category:  categoryBreadcrumbs.at(0), //Only some of these will have values, others will be undefined and there won't be IndexOutOfRange exception
                item_category2: categoryBreadcrumbs.at(1),
                item_category3: categoryBreadcrumbs.at(2),
                item_category4: categoryBreadcrumbs.at(3),
                item_category5: categoryBreadcrumbs.at(4),
                item_category6: categoryBreadcrumbs.at(5),
                item_category7: categoryBreadcrumbs.at(6),
                price: price?.amount,
                quantity: lineItem.quantity,
            };

            gtmItems.push(gtmItem);
        }

        const realOrderSubTotal = order.subTotal;
        let usdOrderSubTotalAmount : number = 0;

        if (realOrderSubTotal?.currency?.code === "USD")
        {
            usdOrderSubTotalAmount = realOrderSubTotal.amount;
        }
        else
        {
            for (const item of order.items || [])
            {
                const itemUSDPrice = item.product?.allCurrenciesPricesTL.find( price => price.list.currency.code === "USD" )?.list?.amount;

                if (!itemUSDPrice || isNaN(itemUSDPrice))
                {
                    Logger.warn(`GTM - _gtmSendCheckoutEvent: Item ${item.product?.code} has no USD price, skipping...`);
                    continue;
                }

                usdOrderSubTotalAmount += itemUSDPrice * item.quantity;
            }
        }


        const orderEventData : IGTMOrderEvent = {
            event: eventName,
            ecommerce: {
                transaction_id: eventName === "quote" ? order.number : order.purchaseOrderNumber,
                shipping: order.shippingTotal?.amount || 0,
                tax: order.taxTotal?.amount || 0,
                currency: "USD", //Always use USD for GTM events, optionally add real currency in custom fields
                value: usdOrderSubTotalAmount,
                items: gtmItems,
            }
        };

        if (realOrderSubTotal?.currency.code !== "USD")
        {
            //Add foreign currency as custom fields to the GTM event
            orderEventData.foreignCurrencyCode = realOrderSubTotal?.currency.code;
            orderEventData.foreignCurrencyAmount = realOrderSubTotal?.amount;
        }

        gtm.push(orderEventData);
    }//End of _gtmSendCheckoutEvent()

    //Exported composable function
    const gtmSendTabViewEvent = (tabName : string) =>
    {
        gtm.push(
            {
                event: "tab_view",
                tab_name: tabName
             });
    }

    //Exported composable function
    const gtmSendSearchEvent = (searchTerm : string) =>
    {
        gtm.push(
            {
                event: "search",
                search_term: searchTerm,
            });
    };//End of gtmSendSearchEvent()

    //Exported composable function
    const gtmSendViewCartEventAsync = async () =>
    {
        await _gtmSendCartEventAsync("view_cart");
    };

    //Exported composable function
    const gtmSendBeginCheckoutEventAsync = async () =>
    {
        await _gtmSendCartEventAsync("begin_checkout");
    };

    //Exported composable function
    const gtmSendAddShippingInfoEventAsync = async (shippingTier : string) =>
    {
        const cartEventData = await _gtmPrepareCartEventAsync("add_shipping_info");
        cartEventData.ecommerce!.shipping_tier = shippingTier;
        cartEventData.ecommerce!.shipping = cart.value?.shippingTotal?.amount || 0;
        cartEventData.ecommerce!.tax = cart.value?.taxTotal?.amount || 0;
        gtm.push(cartEventData);
    }

    //Exported composable function
    const gtmSendAddPaymentInfoEventAsync = async (paymentType : string) =>
    {
        const cartEventData = await _gtmPrepareCartEventAsync("add_payment_info");
        cartEventData.ecommerce!.payment_type = paymentType;
        gtm.push(cartEventData);
    }

    //Exported composable function
    const gtmSendPurchaseEvent = (order : CustomerOrderTypeExtension) =>
    {
        _gtmSendCheckoutEvent(order, "purchase");
    };

    //Exported composable function
    const gtmSendQuoteEvent = (order : CustomerOrderTypeExtension) =>
    {
        _gtmSendCheckoutEvent(order, "quote");
    }

    //Exported composable function
    const gtmSendEmptyCartEventAsync = async () =>
    {
        await _gtmSendCartEventAsync("empty_cart");
    };

    //Exported composable function
    const gtmSendForwardCartEventAsync = async () =>
    {
        await _gtmSendCartEventAsync("forward_cart");
    };

    //Exported composable function
    const gtmSendSaveCartEventAsync = async () =>
    {
        await _gtmSendCartEventAsync("save_cart");
    };

    //Exported composable function
    const gtmSendExportCartEventAsync = async () =>
    {
        await _gtmSendCartEventAsync("export_cart");
    };

    //Exported composable function
    const gtmSendImportCartEventAsync = async () =>
    {
        await _gtmSendCartEventAsync("import_cart");
    };

    //TODO - use this on contact forms and possibly other places
    //Exported composable function
    const gtmSendGenerateLeadEvent = (leadSource? : string) =>
    {
        const cartEventData = _gtmPrepareCartEventWithoutItems("generate_lead");
        const leadEventData = {
            lead_source: leadSource,
            form_name: leadSource,
            form_type: "lead",
            ...cartEventData
        };
        gtm.push(leadEventData);
    };

    //Exported composable function
    const gtmSendFeedbackFormClosed = (formName? : string) =>
    {
        const eventData = {
            event: "feedback_form_closed",
            form_name: formName,
        };
        gtm.push(eventData);
    };

    //Exported composable function
    const gtmSendFormStartEvent = (
        formId?: string,
        formName?: string,
        firstFieldId?: string,
        firstFieldName?: string,
        firstFieldType?: string,
        firstFieldPosition?: number,
        formDestination?: string,
        formLength?: number,
    ) => {
        const eventData = {
            event: "form_start_custom",
            form_id: formId,
            form_name: formName,
            form_type: "lead",
            first_field_id: firstFieldId,
            first_field_name: firstFieldName,
            first_field_type: firstFieldType,
            first_field_position: firstFieldPosition,
            form_destination: formDestination,
            form_length: formLength,
        };
        gtm.push(eventData);
    };

    //Exported composable function
    const gtmSendAutocompleteSearchEvent = (searchTerm : string) =>
    {
        gtm.push(
            {
                event: "autocomplete-search",
                search_term: searchTerm,
            });
    };

    return {
        gtmSendTabViewEvent,
        gtmSendSearchEvent,
        gtmSendViewCartEventAsync,
        gtmSendBeginCheckoutEventAsync,
        gtmSendAddShippingInfoEventAsync,
        gtmSendAddPaymentInfoEventAsync,
        gtmSendPurchaseEvent,
        gtmSendEmptyCartEventAsync,
        gtmSendForwardCartEventAsync,
        gtmSendSaveCartEventAsync,
        gtmSendExportCartEventAsync,
        gtmSendImportCartEventAsync,
        gtmSendQuoteEvent,
        gtmSendGenerateLeadEvent,
        gtmSendAutocompleteSearchEvent,
        gtmSendFeedbackFormClosed,
        gtmSendFormStartEvent
    };
}

export function getAnalyticsCartSubtotalForCurrency(cart : ComputedRef<CartExtension | undefined>) : number | undefined
{
    const currency = cart?.value?.subTotal?.currency || cart?.value?.subTotalWithTax?.currency;

    if (currency?.code === "USD")
        return cart?.value?.subTotal.amount;

    let subTotal : number = 0;

    for (const item of cart.value?.items || [])
    {
        const itemUSDPrice = item.product?.allCurrenciesPricesTL.find( price => price.list.currency.code === "USD" )?.list?.amount;

        if (!itemUSDPrice || isNaN(itemUSDPrice))
        {
            Logger.debug(`GTM - getAnalyticsCartSubtotalForCurrency: Item ${item.product?.code} has no USD price, skipping...`);
            Logger.debug('All currency prices for item');
            Logger.debug(item.product?.allCurrenciesPricesTL);
            continue;
        }

        subTotal += itemUSDPrice * item.quantity;
    }

    return subTotal;
}

export interface IGTMCartItem
{
    item_id?: string;
    item_name?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_category6?: string;
    item_category7?: string;
    price?: number;
    quantity?: number;
    foreignCurrencyCode?: string;
    foreignCurrencyAmount?: number;
}

export interface IGTMCartEcommerce
{
  currency: string;
  value?: number;
  items?: IGTMCartItem[];
  shipping_tier?: string;
  shipping?: number;
  tax?: number;
  payment_type?: string;
}

export interface IGTMCartEvent
{
    event: string;
    ecommerce?: IGTMCartEcommerce;
    foreignCurrencyCode?: string;
    foreignCurrencyAmount?: number;
}

export interface IGTMOrderEcommerce
{
  transaction_id?: string;
  shipping: number;
  tax: number;
  currency: string;
  value?: number;
  items?: IGTMCartItem[];
}

export interface IGTMOrderEvent
{
    event: string;
    ecommerce: IGTMOrderEcommerce;
    foreignCurrencyCode?: string;
    foreignCurrencyAmount?: number;
}
